import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, Alert } from 'react-native';
import { Card, Button, ActivityIndicator, Checkbox, TextInput, Divider, RadioButton } from 'react-native-paper';
import { useNavigation, useRoute } from '@react-navigation/native';
import api from '../../services/api';

const PagoComisionesScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const preselectedVendedorId = route.params?.vendedorId;

  const [vendedores, setVendedores] = useState([]);
  const [selectedVendedor, setSelectedVendedor] = useState(preselectedVendedorId || null);
  const [comisiones, setComisiones] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [referencia, setReferencia] = useState('');
  const [notas, setNotas] = useState('');
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    loadVendedores();
  }, []);

  useEffect(() => {
    if (selectedVendedor) {
      loadComisiones();
    }
  }, [selectedVendedor]);

  const loadVendedores = async () => {
    try {
      const data = await api.getComisionesReporte({});
      setVendedores(data.filter(v => parseFloat(v.total_aprobada) > 0));
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const loadComisiones = async () => {
    try {
      setLoading(true);
      const data = await api.getComisiones({ vendedor_id: selectedVendedor, estado: 'aprobada' });
      setComisiones(data);
      setSelectedIds(data.map(c => c.id));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    setSelectedIds(comisiones.map(c => c.id));
  };

  const deselectAll = () => {
    setSelectedIds([]);
  };

  const montoTotal = comisiones
    .filter(c => selectedIds.includes(c.id))
    .reduce((sum, c) => sum + parseFloat(c.monto_total), 0);

  const handlePagar = () => {
    if (selectedIds.length === 0) {
      Alert.alert('Error', 'Seleccione al menos una comisión');
      return;
    }

    Alert.alert(
      'Confirmar Pago',
      `¿Pagar L. ${montoTotal.toFixed(2)} al vendedor? (${selectedIds.length} comisiones)`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Pagar', onPress: async () => {
            try {
              setPaying(true);
              await api.pagarComisiones({
                vendedor_id: selectedVendedor,
                comision_ids: selectedIds,
                metodo_pago: metodoPago,
                referencia: referencia || null,
                notas: notas || null,
              });
              Alert.alert('Pago Exitoso', `Se pagaron L. ${montoTotal.toFixed(2)}`);
              navigation.goBack();
            } catch (error) {
              Alert.alert('Error', error.message);
            } finally {
              setPaying(false);
            }
          }
        }
      ]
    );
  };

  if (loading && vendedores.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007bff" />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      {/* Selección de vendedor */}
      {!preselectedVendedorId && (
        <Card style={styles.card}>
          <Card.Title title="Seleccionar Vendedor" />
          <Card.Content>
            {vendedores.length === 0 ? (
              <Text style={styles.emptyText}>No hay vendedores con comisiones aprobadas</Text>
            ) : (
              vendedores.map(v => (
                <RadioButton.Item
                  key={v.vendedor_id}
                  label={`${v.vendedor_nombre} - L. ${parseFloat(v.total_aprobada).toFixed(2)} aprobada`}
                  value={v.vendedor_id.toString()}
                  status={selectedVendedor === v.vendedor_id ? 'checked' : 'unchecked'}
                  onPress={() => setSelectedVendedor(v.vendedor_id)}
                />
              ))
            )}
          </Card.Content>
        </Card>
      )}

      {/* Lista de comisiones */}
      {selectedVendedor && (
        <>
          <Card style={styles.card}>
            <Card.Title
              title={`Comisiones Aprobadas (${comisiones.length})`}
              right={() => (
                <View style={styles.selectButtons}>
                  <Button compact onPress={selectAll}>Todas</Button>
                  <Button compact onPress={deselectAll}>Ninguna</Button>
                </View>
              )}
            />
            <Card.Content>
              {loading ? (
                <ActivityIndicator style={{ padding: 20 }} />
              ) : comisiones.length === 0 ? (
                <Text style={styles.emptyText}>No hay comisiones aprobadas</Text>
              ) : (
                comisiones.map(c => (
                  <View key={c.id} style={styles.comisionItem}>
                    <Checkbox
                      status={selectedIds.includes(c.id) ? 'checked' : 'unchecked'}
                      onPress={() => toggleSelection(c.id)}
                    />
                    <View style={styles.comisionInfo}>
                      <Text style={styles.comisionTitle}>Venta #{c.venta_id}</Text>
                      <Text style={styles.comisionDate}>{new Date(c.created_at).toLocaleDateString('es-HN')}</Text>
                    </View>
                    <Text style={styles.comisionMonto}>L. {parseFloat(c.monto_total).toFixed(2)}</Text>
                  </View>
                ))
              )}
            </Card.Content>
          </Card>

          {/* Método de pago */}
          <Card style={styles.card}>
            <Card.Title title="Detalles del Pago" />
            <Card.Content>
              <Text style={styles.fieldLabel}>Método de pago:</Text>
              <View style={styles.metodoRow}>
                {['efectivo', 'transferencia', 'cheque'].map(m => (
                  <RadioButton.Item
                    key={m}
                    label={m.charAt(0).toUpperCase() + m.slice(1)}
                    value={m}
                    status={metodoPago === m ? 'checked' : 'unchecked'}
                    onPress={() => setMetodoPago(m)}
                    style={styles.radioItem}
                  />
                ))}
              </View>

              <TextInput
                label="Referencia (opcional)"
                value={referencia}
                onChangeText={setReferencia}
                mode="outlined"
                style={styles.input}
              />

              <TextInput
                label="Notas (opcional)"
                value={notas}
                onChangeText={setNotas}
                mode="outlined"
                multiline
                style={styles.input}
              />
            </Card.Content>
          </Card>

          {/* Total y botón pagar */}
          <Card style={styles.card}>
            <Card.Content>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Total a pagar:</Text>
                <Text style={styles.totalValue}>L. {montoTotal.toFixed(2)}</Text>
              </View>
              <Text style={styles.totalSubtitle}>{selectedIds.length} comisiones seleccionadas</Text>
              <Button
                mode="contained"
                onPress={handlePagar}
                loading={paying}
                disabled={paying || selectedIds.length === 0}
                style={styles.pagarButton}
                icon="cash"
              >
                Realizar Pago
              </Button>
            </Card.Content>
          </Card>
        </>
      )}

      <View style={{ height: 20 }} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  card: { margin: 8, elevation: 2 },
  emptyText: { textAlign: 'center', color: '#999', padding: 20 },
  selectButtons: { flexDirection: 'row', marginRight: 8 },
  comisionItem: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#eee' },
  comisionInfo: { flex: 1, marginLeft: 8 },
  comisionTitle: { fontSize: 14, fontWeight: '600', color: '#333' },
  comisionDate: { fontSize: 12, color: '#666' },
  comisionMonto: { fontSize: 15, fontWeight: 'bold', color: '#28A745' },
  fieldLabel: { fontSize: 14, fontWeight: '600', color: '#333', marginBottom: 4 },
  metodoRow: {},
  radioItem: { paddingVertical: 2 },
  input: { marginTop: 8 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  totalLabel: { fontSize: 18, fontWeight: '600', color: '#333' },
  totalValue: { fontSize: 24, fontWeight: 'bold', color: '#28A745' },
  totalSubtitle: { fontSize: 13, color: '#666', marginBottom: 12 },
  pagarButton: { backgroundColor: '#28A745', marginTop: 8 },
});

export default PagoComisionesScreen;
